import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Map as MapLibreMap,
  Marker,
  LngLatBounds,
  NavigationControl,
  FullscreenControl,
  Popup,
} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import LocationMarkerSvg from './MapMarker/LocationMarkerSvg';
import { UserLocation } from '../../pages/locations/index';
import UserMarkerSvg from './MapMarker/UserMarkerSvg';
import MapCard from './MapCard';
import ReactDOMServer from 'react-dom/server';
import { Location } from '../../pages/locations/index';
import InfoCard from './InfoCard';

export interface IconDetails {
  title?: string;
  hideEyeOnPin: boolean;
  markerIcon: string;
  classNames?: string;
  focused?: boolean;
}

interface MapOption {
  fullscreenControl?: boolean;
  zoomControl?: boolean;
  unBlockOnClick?: boolean;
  zoom?: number;
}
interface MapProps {
  userLocation?: UserLocation | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  foundLocations?: Location[];
  activeLocation?: Location | null;
  showPopUp?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setActiveLocation: any;
  defaultCenter?: { lat: number; lng: number };
  iconDetails?: IconDetails;
  mapOptions?: MapOption;
  renderInfoCard?: string;
  onMapLoaded?(map: MapLibreMap): void;
}

export default function MapComponent({
  userLocation,
  foundLocations,
  activeLocation,
  setActiveLocation,
  showPopUp,
  defaultCenter,
  iconDetails,
  renderInfoCard,
  mapOptions = { fullscreenControl: true, zoomControl: true, unBlockOnClick: true, zoom: 4 },
  ...props
}: MapProps): React.ReactElement {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const currentPopupRef = useRef<Popup | null>(null);

  const initializeMap = (): void => {
    if (mapContainerRef.current) {
      const map = new MapLibreMap({
        container: mapContainerRef.current,
        // style: 'https://demotiles.maplibre.org/style.json', For default styling
        style: `https://maps.geo.us-east-1.amazonaws.com/maps/v0/maps/LocationServiceMap/style-descriptor?key=${process.env.NEXT_PUBLIC_AWS_LOCATION}`,
        center: defaultCenter ? [defaultCenter.lng, defaultCenter.lat] : [0, 0],
        zoom: mapOptions.zoom || 4,
      });

      mapRef.current = map;

      const navControl = new NavigationControl({
        showCompass: false,
        showZoom: true,
      });

      if (mapOptions.zoomControl) mapRef.current.addControl(navControl, 'bottom-right');

      mapRef.current.scrollZoom.enable();
      mapRef.current.touchZoomRotate.enable();
      mapRef.current.touchPitch.enable();
      mapRef.current.dragPan.enable();

      mapRef.current.getCanvas().style.cursor = 'grab';

      if (mapOptions?.fullscreenControl) {
        const fullscreenControl = new FullscreenControl();
        mapRef.current.addControl(fullscreenControl, 'top-right');
      }
      if (props.onMapLoaded) props.onMapLoaded(map);

      map.on('load', () => {
        setMapBounds();
        addMarkers();
      });
    }
  };

  const addMarkers = (): void => {
    // Remove existing markers
    const existingMarkers = document.querySelectorAll('.marker');
    existingMarkers.forEach((marker) => marker.remove());

    if (userLocation?.map?.lng && userLocation?.map?.lat) {
      const userLocationMarkerElement = document.createElement('div');
      userLocationMarkerElement.className = 'marker';

      // Render the user location marker component
      ReactDOM.render(
        <UserMarkerSvg classNames="text-secondary-actual" />,
        userLocationMarkerElement
      );

      userLocationMarkerElement.style.width = '40px';
      userLocationMarkerElement.style.height = '40px';

      // Add marker to map
      new Marker({ element: userLocationMarkerElement })
        .setLngLat([userLocation.map.lng, userLocation.map.lat])
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .addTo(mapRef.current!);
    }

    (foundLocations || []).forEach((loc) => {
      if (document.querySelector(`[data-slug="${loc.slug}"]`)) return;

      const locationMarkerElement = document.createElement('div');
      locationMarkerElement.className = 'marker';
      locationMarkerElement.dataset.slug = loc.slug;

      locationMarkerElement.style.zIndex =
        JSON.stringify(activeLocation) === JSON.stringify(loc) ? '20' : '1';

      // Render the custom marker component
      ReactDOM.render(
        <LocationMarkerSvg
          title={iconDetails?.title || `Location: ${loc.name}`}
          hideEyeOnPin={iconDetails?.hideEyeOnPin || false}
          markerIcon={iconDetails?.markerIcon || 'Eye-V01'}
          focused={activeLocation?.slug === loc.slug}
          classNames={iconDetails?.classNames}
        />,
        locationMarkerElement
      );

      locationMarkerElement.style.width = '40px';
      locationMarkerElement.style.height = '40px';

      new Marker({ element: locationMarkerElement })
        .setLngLat([loc.map.lon, loc.map.lat])
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        .addTo(mapRef.current!);

      if (mapOptions?.unBlockOnClick) {
        locationMarkerElement.addEventListener('click', () => {
          setActiveLocation(activeLocation !== loc ? loc : null);

          // Center the map on the clicked location
          mapRef.current?.setCenter([loc.map.lon, loc.map.lat]);
        });
      }
    });
  };

  const setMapBounds = (): void => {
    if (!mapRef.current || (foundLocations || []).length === 0) return;

    const bounds = new LngLatBounds();

    (foundLocations || []).forEach((loc) => {
      bounds.extend([loc.map.lon, loc.map.lat]);
    });

    if (userLocation) {
      bounds.extend([userLocation.map.lng, userLocation.map.lat]);
    }

    mapRef.current.fitBounds(bounds, {
      padding: 100,
      maxZoom: 15,
      duration: 1000,
    });
  };

  useEffect(() => {
    initializeMap();

    return () => {
      if (mapRef.current) mapRef.current.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    addMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation, foundLocations, activeLocation]);

  useEffect(() => {
    if (
      currentPopupRef.current &&
      !showPopUp &&
      currentPopupRef?.current?.getLngLat().lat !== activeLocation?.map.lat &&
      currentPopupRef?.current?.getLngLat().lng !== activeLocation?.map.lon
    ) {
      currentPopupRef.current.remove();
    }

    if (activeLocation && showPopUp) {
      const popupContent = ReactDOMServer.renderToString(
        renderInfoCard === 'MapCard' ? (
          <MapCard location={activeLocation} />
        ) : (
          <InfoCard
            fields={activeLocation}
            slug={activeLocation.slug}
            current={activeLocation.current}
          />
        )
      );
      const newPopup = new Popup({ offset: [-20, -20], closeButton: false, anchor: 'top' })
        .setLngLat([activeLocation.map.lon, activeLocation.map.lat])
        .setHTML(popupContent)
        .addTo(mapRef.current!);

      const popupElement = newPopup.getElement();
      if (popupElement) {
        popupElement.style.zIndex = '22'; // Set z-index above 20 (you can adjust the value as needed)
      }

      // Update the current popup reference
      currentPopupRef.current = newPopup;
    }
  }, [activeLocation, showPopUp]);

  return <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />;
}
